import { c as _c } from "react/compiler-runtime";
import { Stack, styled, Tooltip, Typography } from '@mui/material';
import { ConditionOperatorEnum, type Condition } from '@repo/api-gw-sdk';
import classNames from 'classnames';
import React from 'react';
import { ApplicationsLogosWithAliases } from '@/assets/logos/applications';
import { CloudProviders } from '@/assets/logos/cloudProviders';
import { useSourceAccounts } from '@/contexts/useSourceAccounts';
import { EncryptionTypes, Environments, NetworkClassifications, SensitivityLevels } from '@/data/inventory/resourceClassifications';
import { ResourceTypes } from '@/data/inventory/resourceTypes';
import { type FilterProperty } from '@/types/advanceFilter';
import { fileSizeFormatter } from '@/utils/fileSizeFormatter';
import { OPERATORS } from './OperatorSelect';
import { SECURITY_SCAN_CONCLUSION_OPTIONS } from '../security/detectorOptions';
import { CircleImage } from '../shared/circleImage';
const ROW_GAP = 12;
const securityScanConclusionLabels: ReadonlyMap<string, string> = new Map(SECURITY_SCAN_CONCLUSION_OPTIONS.map(({
  id,
  name
}) => [id, name]));
export const ConditionContainer = styled('div', {
  shouldForwardProp: prop => prop !== 'hideBorder' && prop !== 'enableHoverIndications'
})(({
  hideBorder,
  enableHoverIndications
}: {
  hideBorder?: boolean;
  enableHoverIndications?: boolean;
}) => ({
  width: 'fit-content',
  gap: '4px',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'start',
  border: hideBorder ? undefined : '1px solid var(--mui-palette-divider)',
  borderRadius: '4px',
  padding: '12px 16px',
  fontWeight: 600,
  position: 'relative',
  [`&.${ConditionOperatorEnum.And},&.${ConditionOperatorEnum.Or}`]: {
    alignItems: 'flex-start',
    gap: ROW_GAP
  },
  '&.hovered': {
    borderColor: 'var(--mui-palette-primary-main)'
  },
  ...(enableHoverIndications ? {
    '&:hover': {
      borderColor: 'var(--mui-palette-primary-main)'
    }
  } : {}),
  '&.root': {
    border: 'none',
    padding: 0,
    flexDirection: 'column'
  },
  '&.highlighted-above::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    left: 0,
    right: 0,
    top: -7,
    borderTop: '1px solid var(--mui-palette-primary-main)',
    height: 1
  },
  '&.highlighted-below::after': {
    content: '""',
    display: 'block',
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: -7,
    borderTop: '1px solid var(--mui-palette-primary-main)',
    height: 1
  },
  '&.compact': {
    padding: 0,
    border: 0,
    '> img': {
      height: '100%'
    }
  }
}));
export const ConditionGroup = styled(ConditionContainer)({
  paddingLeft: '50px',
  alignItems: 'flex-start',
  flexDirection: 'column',
  gap: ROW_GAP,
  '&.compact': {
    gap: 6
  }
});
const ConditionOperator = styled('div')({
  position: 'absolute',
  left: '-40px',
  top: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: '4px',
  transform: `translateY(calc(-50% + ${ROW_GAP / 2}px))`,
  fontWeight: 400,
  fontSize: 12,
  '&::before': {
    content: '""',
    display: 'block',
    width: '12px',
    height: '12px',
    borderRadius: '4px 0 0 0',
    borderColor: 'currentColor',
    borderWidth: '1px 0 0 1px'
  },
  '&::after': {
    content: '""',
    display: 'block',
    width: '12px',
    height: '12px',
    borderRadius: '0 0 0 4px',
    borderColor: 'currentColor',
    borderWidth: '0 0 1px 1px'
  }
});
const ConditionValue = t0 => {
  const $ = _c(52);
  const {
    value,
    property
  } = t0;
  const {
    sourceAccounts
  } = useSourceAccounts();
  if (property === "apps") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = {
        gap: "4px"
      };
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== value) {
      t2 = value?.map(_temp);
      $[1] = value;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] !== t2) {
      t3 = <Stack direction="row" className="mt-[-4px]" sx={t1}>{t2}</Stack>;
      $[3] = t2;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  if (property === "environment") {
    let t1;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = {
        color: "currentcolor"
      };
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    let t2;
    if ($[6] !== value) {
      t2 = value?.map(_temp2).join(", ");
      $[6] = value;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    let t3;
    if ($[8] !== t2) {
      t3 = <Typography variant="subtitle2" className="font-medium" sx={t1}>{t2}{" "}</Typography>;
      $[8] = t2;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    return t3;
  }
  if (property === "networkClassification") {
    let t1;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = {
        color: "currentcolor"
      };
      $[10] = t1;
    } else {
      t1 = $[10];
    }
    let t2;
    if ($[11] !== value) {
      t2 = value?.map(_temp3).join(", ");
      $[11] = value;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    let t3;
    if ($[13] !== t2) {
      t3 = <Typography variant="subtitle2" className="font-medium" sx={t1}>{t2}</Typography>;
      $[13] = t2;
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    return t3;
  }
  if (property === "resourceType") {
    let t1;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = {
        color: "currentcolor"
      };
      $[15] = t1;
    } else {
      t1 = $[15];
    }
    let t2;
    if ($[16] !== value) {
      t2 = value?.map(_temp4).filter(Boolean).join(", ");
      $[16] = value;
      $[17] = t2;
    } else {
      t2 = $[17];
    }
    let t3;
    if ($[18] !== t2) {
      t3 = <Typography variant="subtitle2" className="font-medium" sx={t1}>{t2}</Typography>;
      $[18] = t2;
      $[19] = t3;
    } else {
      t3 = $[19];
    }
    return t3;
  }
  if (property === "cloudProvider") {
    let t1;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = {
        color: "currentcolor"
      };
      $[20] = t1;
    } else {
      t1 = $[20];
    }
    let t2;
    if ($[21] !== value) {
      t2 = value?.map(_temp5).filter(Boolean).join(", ");
      $[21] = value;
      $[22] = t2;
    } else {
      t2 = $[22];
    }
    let t3;
    if ($[23] !== t2) {
      t3 = <Typography variant="subtitle2" className="font-medium" sx={t1}>{t2}</Typography>;
      $[23] = t2;
      $[24] = t3;
    } else {
      t3 = $[24];
    }
    return t3;
  }
  if (property === "sourceStorageSize") {
    let t1;
    if ($[25] !== value) {
      t1 = value?.map(_temp6).join(", ");
      $[25] = value;
      $[26] = t1;
    } else {
      t1 = $[26];
    }
    let t2;
    if ($[27] !== t1) {
      t2 = <Typography color="currentColor" variant="subtitle2Strong">{t1}</Typography>;
      $[27] = t1;
      $[28] = t2;
    } else {
      t2 = $[28];
    }
    return t2;
  }
  if (property === "sensitivityLabels") {
    let t1;
    if ($[29] !== value) {
      t1 = value?.map(_temp7).join(", ");
      $[29] = value;
      $[30] = t1;
    } else {
      t1 = $[30];
    }
    let t2;
    if ($[31] !== t1) {
      t2 = <Typography color="currentColor" variant="subtitle2Strong">{t1}</Typography>;
      $[31] = t1;
      $[32] = t2;
    } else {
      t2 = $[32];
    }
    return t2;
  }
  if (property === "encryptionType") {
    let t1;
    if ($[33] !== value) {
      t1 = value?.map(_temp8).join(", ");
      $[33] = value;
      $[34] = t1;
    } else {
      t1 = $[34];
    }
    let t2;
    if ($[35] !== t1) {
      t2 = <Typography color="currentColor" variant="subtitle2Strong">{t1}</Typography>;
      $[35] = t1;
      $[36] = t2;
    } else {
      t2 = $[36];
    }
    return t2;
  }
  if (property === "securityScanConclusion") {
    let t1;
    if ($[37] !== value) {
      t1 = value?.map(_temp9).join(", ");
      $[37] = value;
      $[38] = t1;
    } else {
      t1 = $[38];
    }
    let t2;
    if ($[39] !== t1) {
      t2 = <Typography color="currentColor" variant="subtitle2Strong">{t1}</Typography>;
      $[39] = t1;
      $[40] = t2;
    } else {
      t2 = $[40];
    }
    return t2;
  }
  if (property === "accountId") {
    let t1;
    if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = {
        color: "currentcolor"
      };
      $[41] = t1;
    } else {
      t1 = $[41];
    }
    let t2;
    if ($[42] !== sourceAccounts || $[43] !== value) {
      t2 = value?.map(x_7 => {
        const sourceAccount = sourceAccounts?.find(a => a.providerAccountId === x_7);
        const text = sourceAccount?.name || x_7;
        return text;
      }).filter(Boolean).join(", ");
      $[42] = sourceAccounts;
      $[43] = value;
      $[44] = t2;
    } else {
      t2 = $[44];
    }
    let t3;
    if ($[45] !== t2) {
      t3 = <Typography variant="subtitle2" className="font-medium" sx={t1}>{t2}</Typography>;
      $[45] = t2;
      $[46] = t3;
    } else {
      t3 = $[46];
    }
    return t3;
  }
  let t1;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      color: "currentcolor"
    };
    $[47] = t1;
  } else {
    t1 = $[47];
  }
  let t2;
  if ($[48] !== value) {
    t2 = value?.join(", ");
    $[48] = value;
    $[49] = t2;
  } else {
    t2 = $[49];
  }
  let t3;
  if ($[50] !== t2) {
    t3 = <Typography variant="subtitle2" className="font-medium" sx={t1}>{t2}</Typography>;
    $[50] = t2;
    $[51] = t3;
  } else {
    t3 = $[51];
  }
  return t3;
};
export function ConditionViewer(t0) {
  const $ = _c(32);
  const {
    condition,
    compact,
    supportProperties,
    parent,
    children
  } = t0;
  let t1;
  if ($[0] !== compact || $[1] !== condition.operator) {
    t1 = classNames(condition.operator, {
      compact
    });
    $[0] = compact;
    $[1] = condition.operator;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const cls = t1;
  let t2;
  if ($[3] !== condition.property || $[4] !== supportProperties) {
    let t3;
    if ($[6] !== condition.property) {
      t3 = c => c.name === condition.property;
      $[6] = condition.property;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    t2 = supportProperties.find(t3);
    $[3] = condition.property;
    $[4] = supportProperties;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const property = t2;
  switch (condition.operator) {
    case ConditionOperatorEnum.And:
    case ConditionOperatorEnum.Or:
      {
        const t3 = !parent;
        let t4;
        if ($[8] !== compact || $[9] !== condition || $[10] !== supportProperties) {
          t4 = condition.conditions?.map((c_0, i, arr) => <ConditionViewer supportProperties={supportProperties} compact={compact} parent={condition} condition={c_0} key={i}>{i < arr.length - 1 && !compact && <ConditionOperator>{condition.operator.toUpperCase()}</ConditionOperator>}</ConditionViewer>);
          $[8] = compact;
          $[9] = condition;
          $[10] = supportProperties;
          $[11] = t4;
        } else {
          t4 = $[11];
        }
        let t5;
        if ($[12] !== children || $[13] !== cls || $[14] !== t3 || $[15] !== t4) {
          t5 = <ConditionGroup hideBorder={t3} className={cls}>{t4}{children}</ConditionGroup>;
          $[12] = children;
          $[13] = cls;
          $[14] = t3;
          $[15] = t4;
          $[16] = t5;
        } else {
          t5 = $[16];
        }
        return t5;
      }
    default:
      {
        let t3;
        if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
          t3 = {
            color: "currentcolor"
          };
          $[17] = t3;
        } else {
          t3 = $[17];
        }
        const t4 = property?.condition?.displayName ?? property?.displayName ?? condition.property ?? "";
        let t5;
        if ($[18] !== t4) {
          t5 = <Typography className="whitespace-nowrap" variant="subtitle2Strong" sx={t3}>{t4}</Typography>;
          $[18] = t4;
          $[19] = t5;
        } else {
          t5 = $[19];
        }
        let t6;
        if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
          t6 = {
            color: "currentcolor"
          };
          $[20] = t6;
        } else {
          t6 = $[20];
        }
        const t7 = property?.policyRelation && OPERATORS[property.policyRelation][condition.operator as ConditionOperatorEnum]?.label || condition.operator;
        let t8;
        if ($[21] !== t7) {
          t8 = <Typography className="whitespace-nowrap" variant="subtitle2" sx={t6}> {t7} </Typography>;
          $[21] = t7;
          $[22] = t8;
        } else {
          t8 = $[22];
        }
        let t9;
        if ($[23] !== condition.property || $[24] !== condition.value) {
          t9 = <ConditionValue property={condition.property} value={condition.value} />;
          $[23] = condition.property;
          $[24] = condition.value;
          $[25] = t9;
        } else {
          t9 = $[25];
        }
        let t10;
        if ($[26] !== children || $[27] !== cls || $[28] !== t5 || $[29] !== t8 || $[30] !== t9) {
          t10 = <ConditionContainer className={cls}>{t5}{t8}{t9}{children}</ConditionContainer>;
          $[26] = children;
          $[27] = cls;
          $[28] = t5;
          $[29] = t8;
          $[30] = t9;
          $[31] = t10;
        } else {
          t10 = $[31];
        }
        return t10;
      }
  }
}
function _temp(v, i) {
  return <Tooltip title={v} key={i}><CircleImage alt={v} src={ApplicationsLogosWithAliases[v]} /></Tooltip>;
}
function _temp2(x) {
  return Environments[x]?.title;
}
function _temp3(x_0) {
  return NetworkClassifications[x_0]?.title ?? x_0;
}
function _temp4(x_1) {
  return ResourceTypes[x_1]?.displayName;
}
function _temp5(x_2) {
  return CloudProviders[x_2]?.displayName;
}
function _temp6(x_3) {
  return fileSizeFormatter(Number(x_3));
}
function _temp7(x_4) {
  return SensitivityLevels[x_4]?.title ?? x_4;
}
function _temp8(x_5) {
  return EncryptionTypes[x_5]?.title ?? x_5;
}
function _temp9(x_6) {
  return securityScanConclusionLabels.get(x_6) ?? x_6;
}